import { useState } from 'react'
import {
    useNavigate,
    useParams,
    useSearchParams,
} from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import { toast } from 'react-toastify'
import posthog from 'posthog-js'
import { Modal } from 'react-bootstrap'
import Header from 'components/headers/addLoan'
import { PulseCard, SectionError } from 'components/states'
import {
    closeWithdrawalRequest,
    getWithdrawalRequest,
} from 'services/deposits'
import { faPlain, longJalali, toman } from 'utilities/helper/format'

const EVENT_LABELS: Record<IWithdrawalEventKind, string> = {
    crt: 'درخواست ثبت شد',
    ntf: 'به مدیران صندوق اطلاع داده شد',
    pay: 'پرداخت ثبت شد',
    not: 'یادداشت مدیر',
    cls: 'دریافت را تأیید کردید',
    cnl: 'درخواست را بستید',
}

const STATE_LABELS: Record<IWithdrawalState, string> = {
    wdrq: 'در انتظار پرداخت',
    wdpd: 'پرداخت شد — منتظر تأیید شما',
    wdcm: 'تکمیل شد',
    wdcn: 'بسته شد',
}

/**
 * One درخواست برداشت and everything that has happened to it.
 *
 * This page is the answer to the silence. A member files a request, hears
 * «ثبت شد», and then nothing — and from the outside a fund that is quietly
 * paying in instalments looks exactly like a fund that has forgotten. The
 * timeline is the feature; the amounts at the top are the summary of it.
 *
 * It is also the target of the پیگیری link in every SMS, reached through the
 * backend's short `/w/<id>/` redirect.
 */
export default function WithdrawRequestDetail() {
    const params = useParams()
    const navigate = useNavigate()
    const queryClient = useQueryClient()
    const [searchParams] = useSearchParams()
    const [closing, setClosing] = useState(false)

    const requestId = params?.id
    const fundId = searchParams.get('fund')

    const { data, isLoading, isError, refetch } = useQuery(
        ['withdrawal-request', fundId, requestId],
        getWithdrawalRequest,
        { enabled: Boolean(fundId && requestId) }
    )
    // The API route puts the fund in the path, so without `?fund=` there is no
    // URL to call and the query above stays disabled — which also means
    // `isLoading` is FALSE. Rendering off `isLoading || data` alone therefore
    // produced a completely blank page, and that is exactly what the one link
    // we put in an SMS did before the backend started appending the parameter.
    // A disabled query must never read as "nothing to show".
    const missingFund = !fundId

    const { mutate: close, isLoading: isClosing } = useMutation({
        mutationFn: (received: boolean) =>
            closeWithdrawalRequest({
                moneypoolId: fundId as string,
                requestId: requestId as string,
                received,
            }),
        onSuccess: (_result, received) => {
            posthog.capture('withdrawal_request_closed', {
                fund_id: fundId,
                reason: received ? 'received' : 'no_longer_needed',
            })
            setClosing(false)
            queryClient.invalidateQueries(['withdrawal-request'])
            queryClient.invalidateQueries(['my-deposits'])
            toast.success(
                received ? 'دریافت تأیید شد' : 'درخواست شما بسته شد'
            )
        },
        onError: (error: any) => {
            setClosing(false)
            toast.error(error?.response?.data?.params || 'انجام نشد')
        },
    })

    const progress = data
        ? Math.min(100, Math.round((data.paid_amount / data.amount) * 100))
        : 0

    return (
        <div className=" min-h-screen bg-canvas pb-24">
            <Header title="درخواست برداشت" />

            <div className=" px-4 pt-4">
                {missingFund && (
                    <div className=" rounded-2xl border border-stroke bg-white p-6 text-center">
                        <p className=" text-sm leading-7 text-text-body">
                            این نشانی ناقص است و درخواست پیدا نشد.
                        </p>
                        <button
                            type="button"
                            onClick={() => navigate('/home')}
                            className=" mt-4 w-full rounded-full bg-green-1 py-2 text-sm font-bold text-white"
                        >
                            رفتن به صفحهٔ اصلی
                        </button>
                    </div>
                )}
                {!missingFund && isLoading && <PulseCard />}
                {!missingFund && isError && (
                    <SectionError onRetry={() => refetch()} />
                )}

                {data && (
                    <>
                        <div className=" rounded-2xl border border-stroke bg-white p-4">
                            <p className=" text-xs text-text-secondary">
                                مبلغ درخواست
                            </p>
                            <p className=" mt-1 text-[28px] font-bold leading-tight text-heading">
                                {toman(data.amount)}
                            </p>
                            <p className=" mt-1 text-sm text-text-body">
                                {STATE_LABELS[data.state]}
                            </p>

                            {data.paid_amount > 0 && (
                                <>
                                    <div className=" mt-4 h-2 w-full overflow-hidden rounded-full bg-divider">
                                        <div
                                            className=" h-full rounded-full bg-brand"
                                            style={{ width: `${progress}%` }}
                                        />
                                    </div>
                                    <div className=" mt-2 flex items-center justify-between text-[11px]">
                                        <span className=" text-success-on">
                                            پرداخت‌شده{' '}
                                            {toman(data.paid_amount)}
                                        </span>
                                        <span className=" text-text-secondary">
                                            باقیمانده {toman(data.remaining)}
                                        </span>
                                    </div>
                                </>
                            )}

                            {!data.partial_accepted && (
                                <p className=" mt-3 rounded-lg bg-warning-soft px-3 py-2 text-[11px] leading-6 text-warning-on">
                                    شما گفته‌اید فقط همهٔ مبلغ را می‌خواهید، پس
                                    مدیر تا آماده شدن کل مبلغ نمی‌تواند بخشی از
                                    آن را پرداخت کند.
                                </p>
                            )}

                            {data.user_description && (
                                <p className=" mt-3 border-t border-divider pt-3 text-[11px] leading-6 text-text-body">
                                    توضیح شما: {data.user_description}
                                </p>
                            )}
                        </div>

                        <p className=" mb-2 mt-5 px-1 text-sm font-bold text-heading">
                            چه اتفاقی افتاده
                        </p>
                        <ol className=" rounded-2xl border border-stroke bg-white px-4 py-2">
                            {(data.events ?? []).map((event) => (
                                <li
                                    key={event.id}
                                    className=" flex gap-3 border-b border-divider py-3 last:border-b-0"
                                >
                                    <span
                                        aria-hidden
                                        className={` mt-1 h-2 w-2 shrink-0 rounded-full ${
                                            event.kind === 'pay'
                                                ? ' bg-brand'
                                                : ' bg-stroke'
                                        }`}
                                    />
                                    <span className=" min-w-0 flex-1">
                                        <span className=" block text-xs font-bold text-text-body">
                                            {EVENT_LABELS[event.kind]}
                                            {event.amount
                                                ? ` — ${toman(event.amount)}`
                                                : ''}
                                        </span>
                                        <span className=" mt-[2px] block text-[11px] text-text-muted">
                                            {longJalali(event.created)}
                                            {event.actor_name
                                                ? ` · ${event.actor_name}`
                                                : ''}
                                        </span>
                                        {event.note && (
                                            <span className=" mt-1 block text-[11px] leading-6 text-text-secondary">
                                                {event.note}
                                            </span>
                                        )}
                                    </span>
                                </li>
                            ))}
                        </ol>

                        {data.is_open && (
                            <button
                                type="button"
                                onClick={() => setClosing(true)}
                                className=" mt-5 w-full rounded-full border border-green-1 bg-white py-3 text-sm font-bold text-green-1"
                            >
                                {data.remaining === 0
                                    ? 'تأیید دریافت و بستن درخواست'
                                    : 'بستن درخواست'}
                            </button>
                        )}

                        {/* Said out loud, because it is the rule that makes
                            this feature safe to use: nobody else can close
                            this on the member's behalf. */}
                        {data.is_open && (
                            <p className=" mt-2 px-1 text-center text-[11px] leading-6 text-text-muted">
                                تنها خود شما می‌توانید این درخواست را ببندید.
                                مدیر صندوق نمی‌تواند آن را رد کند.
                            </p>
                        )}

                        <button
                            type="button"
                            onClick={() => navigate(`/fund/${fundId}`)}
                            className=" mt-3 w-full py-2 text-xs text-text-secondary"
                        >
                            رفتن به صندوق
                        </button>
                    </>
                )}
            </div>

            <Modal
                show={closing}
                onHide={() => setClosing(false)}
                size="sm"
                centered
            >
                <div className=" w-full px-4 py-4 text-center text-sm">
                    <p className=" mb-3">
                        {data && data.remaining > 0
                            ? `${toman(
                                  data.remaining
                              )} از درخواست شما هنوز پرداخت نشده است. می‌خواهید درخواست را ببندید؟`
                            : 'آیا مبلغ را دریافت کرده‌اید؟'}
                    </p>
                    <hr className=" my-3" />
                    <p
                        role="button"
                        className=" cursor-pointer text-center text-green-1"
                        onClick={() => !isClosing && close(true)}
                    >
                        بله، پول را گرفتم
                    </p>
                    <hr className=" my-3" />
                    <p
                        role="button"
                        className=" cursor-pointer text-center"
                        onClick={() => !isClosing && close(false)}
                    >
                        دیگر لازم ندارم
                    </p>
                    <hr className=" my-3" />
                    <p
                        role="button"
                        className=" cursor-pointer text-center text-text-secondary"
                        onClick={() => setClosing(false)}
                    >
                        بستن
                    </p>
                </div>
            </Modal>
        </div>
    )
}
